import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, ImageIcon } from "lucide-react";
import type { GalleryCategory } from "./categories";

type Props = {
  category: GalleryCategory;
  onExplore?: (category: GalleryCategory) => void;
};

const SLIDE_MS = 6000;

export function CategoryHero({ category, onExplore }: Props) {
  const [index, setIndex] = useState(0);

  // Reset slideshow when the category changes
  useEffect(() => {
    setIndex(0);
  }, [category.id]);

  useEffect(() => {
    if (category.images.length <= 1) return;
    const t = setInterval(
      () => setIndex((i) => (i + 1) % category.images.length),
      SLIDE_MS,
    );
    return () => clearInterval(t);
  }, [category.id, category.images.length]);

  const current = category.images[index];

  return (
    <div className="relative h-[70svh] min-h-[560px] w-full overflow-hidden rounded-2xl bg-navy-deep">
      {/* Slideshow background */}
      <AnimatePresence mode="sync">
        <motion.div
          key={`${category.id}-${index}`}
          initial={{ opacity: 0, scale: 1.08 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ opacity: { duration: 1.2 }, scale: { duration: 6, ease: "linear" } }}
          className="absolute inset-0"
        >
          <img
            src={current}
            alt={`${category.title} – slide ${index + 1}`}
            className="h-full w-full object-cover"
            loading="lazy"
            width={1600}
            height={1024}
          />
        </motion.div>
      </AnimatePresence>

      {/* Dark overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-navy-deep via-navy-deep/70 to-navy-deep/40" />
      <div className="absolute inset-0 bg-black/30" />

      {/* Foreground content */}
      <div className="relative z-10 flex h-full flex-col justify-end p-6 sm:p-10 lg:p-16">
        <AnimatePresence mode="wait">
          <motion.div
            key={category.id}
            initial={{ y: 28, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -16, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-3xl"
          >
            <span className="eyebrow text-orange">{category.label} Projects</span>
            <h3 className="mt-4 text-4xl font-black leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl">
              {category.title}
            </h3>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-white/80 sm:text-lg">
              {category.tagline}
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-4">
             {/*  <button
                onClick={() => onExplore?.(category)}
                className="group inline-flex items-center gap-2 rounded-md bg-orange px-6 py-3 text-sm font-semibold text-white shadow-lg transition-all hover:bg-orange-hover hover:shadow-xl"
              >
                Explore Gallery
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button> */}
              <span className="inline-flex items-center gap-2 rounded-md border border-white/20 bg-white/5 px-4 py-3 text-sm font-medium text-white/85 backdrop-blur">
                <ImageIcon className="h-4 w-4 text-orange" />
                {category.images.length} {category.images.length === 1 ? "image" : "images"}
              </span>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Slide indicators */}
        <div className="mt-8 flex items-center gap-2">
          {category.images.map((_, i) => (
            <button
              key={i}
              aria-label={`Show slide ${i + 1}`}
              onClick={() => setIndex(i)}
              className="group h-1.5 w-10 overflow-hidden rounded-full bg-white/20 cursor-pointer"
            >
              <span
                className={`block h-full bg-orange transition-all duration-300 ${
                  i === index ? "w-full" : "w-0 group-hover:w-1/3"
                }`}
              />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
